import { subDays, subMonths, startOfMonth, endOfMonth, startOfYear, isSameDay } from 'date-fns';

// Shared period presets for RangeDatePickerControl.
//
// Extracted from DataSharing.vue, which had the only copy, so the direct-order screen can reuse the
// exact same ranges instead of redefining "last 3 months" slightly differently a second time.
//
// Each entry is a translation key plus a `range(context)` builder. `custom` deliberately has no
// builder: RangeDatePickerControl treats a preset without a range as the "open the calendar" entry.

export const PERIOD_PRESETS = {
	'last-7-days': { labelKey: 'Last7Days', range: (today) => [subDays(today, 7), today] },
	'last-30-days': { labelKey: 'Last30Days', range: (today) => [subDays(today, 30), today] },
	'this-month': { labelKey: 'ThisMonth', range: (today) => [startOfMonth(today), today] },
	'last-month': { labelKey: 'LastMonth', range: (today) => [startOfMonth(subMonths(today, 1)), endOfMonth(subMonths(today, 1))] },
	'last-3-months': { labelKey: 'Last3Months', range: (today) => [subMonths(today, 3), today] },
	'last-6-months': { labelKey: 'Last6Months', range: (today) => [subMonths(today, 6), today] },
	'last-12-months': { labelKey: 'Last12Months', range: (today) => [subMonths(today, 12), today] },
	'this-year': { labelKey: 'ThisYear', range: (today) => [startOfYear(today), today] },
	// The current objective period: the window the user's objectives are set for, stepped from the KPI's
	// P0 at its configured frequency (sfaHelper getKPIPeriod) — e.g. the current month for a monthly
	// objective. Not Core/Periods/Active, which is the BO's long-running commercial period ("2022-2030").
	// Its bounds come from the caller, who resolves them through the legacy sfaHelper.
	'current-period': { labelKey: 'CurrentPeriod', range: (today, context) => (context?.objectivePeriod?.start ? [new Date(context.objectivePeriod.start), context.objectivePeriod.end ? new Date(context.objectivePeriod.end) : today] : null) },
	custom: { labelKey: 'CustomDates' },
};

export function presetRange(value, context = {}, today = new Date()) {
	const preset = PERIOD_PRESETS[value];
	if (!preset?.range) return null;
	return preset.range(today, context);
}

/**
 * Builds the `presets` array RangeDatePickerControl expects.
 *
 * Presets whose range cannot be resolved (typically `current-period` when the user has no objective)
 * are dropped rather than listed with a null range: the picker would show them as selectable and
 * then do nothing on click.
 *
 * @param {string[]} keys   preset ids, in display order
 * @param {Function} t      translator
 * @param {object}   context extra data some presets need (`objectivePeriod`)
 */
export function buildPresets(keys, t, context = {}) {
	const today = new Date();

	return keys
		.map((key) => {
			const preset = PERIOD_PRESETS[key];
			if (!preset) return null;
			if (!preset.range) return { title: t(preset.labelKey), value: key };

			const range = preset.range(today, context);
			return range ? { title: t(preset.labelKey), value: key, range } : null;
		})
		.filter(Boolean);
}

/**
 * Finds which preset (if any) resolves to exactly the given start/end - so a UI can show "3 derniers
 * mois" instead of raw dates whenever the applied period happens to be a preset's own range, without
 * having to remember which preset was picked. Compared by calendar day, not exact timestamp, since a
 * preset's `range()` stamps `today`'s current time while the caller's dates usually got normalized
 * (start/end of day) somewhere along the way.
 */
export function matchPresetKey(startDate, endDate, keys = Object.keys(PERIOD_PRESETS), context = {}, today = new Date()) {
	if (!startDate || !endDate) return null;
	const start = new Date(startDate);
	const end = new Date(endDate);

	return (
		keys.find((key) => {
			const range = PERIOD_PRESETS[key]?.range?.(today, context);
			return range && isSameDay(range[0], start) && isSameDay(range[1], end);
		}) ?? null
	);
}

export function periodPresetLabel(startDate, endDate, t, keys = Object.keys(PERIOD_PRESETS), context = {}, today = new Date()) {
	const key = matchPresetKey(startDate, endDate, keys, context, today);
	return key ? t(PERIOD_PRESETS[key].labelKey) : null;
}

/**
 * Turns an applied period into something worth persisting. A period that matches a preset is stored
 * as the preset key, so "3 derniers mois" keeps rolling with the calendar instead of freezing on the
 * dates it had the day it was picked; anything else is stored as plain dates.
 */
export function serializePeriodSelection(startDate, endDate, keys, context = {}, today = new Date()) {
	if (!startDate || !endDate) return null;
	const preset = matchPresetKey(startDate, endDate, keys, context, today);
	return preset ? { preset } : { startDate: new Date(startDate).toISOString(), endDate: new Date(endDate).toISOString() };
}

/**
 * Inverse of serializePeriodSelection. Null when nothing usable was stored — including a preset that
 * no longer resolves (e.g. "current-period" once the user has no objective any more), so the caller
 * falls back to its own default instead of showing a broken range.
 */
export function resolvePeriodSelection(saved, context = {}, today = new Date()) {
	if (!saved) return null;
	if (saved.preset) {
		const range = presetRange(saved.preset, context, today);
		return range ? { startDate: range[0], endDate: range[1] } : null;
	}
	const startDate = new Date(saved.startDate);
	const endDate = new Date(saved.endDate);
	return Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()) ? null : { startDate, endDate };
}

// The preset set the direct-order tracking screen offers (RG-01).
export const ORDER_TRACKING_PRESET_KEYS = ['current-period', 'this-month', 'last-month', 'last-3-months', 'last-6-months', 'last-12-months', 'custom'];

// The set DataSharing has always offered — kept verbatim so extracting these helpers changes
// nothing for that screen.
export const DATA_SHARING_PRESET_KEYS = ['last-7-days', 'last-30-days', 'this-month', 'last-month', 'last-3-months', 'last-6-months', 'this-year', 'custom'];
